<script lang="ts">
import { getCurrentInstance, onBeforeMount, ref, shallowRef, watch } from 'vue';
import { IFloatingFilterParams } from 'ag-grid-enterprise';
import Wrapper from './FilterWrapper.vue';
import InputFormatted from './InputFormatted.vue';

interface FilterModel {
  filter: string;
  type: 'equals' | 'contains';
}

export interface LocalFilterModel {
  filter: string;
  type: 'equals' | 'contains';
}
</script>

<script setup lang="ts">
const localFilterModel = ref<LocalFilterModel>({ filter: '', type: 'equals' });

function transform(value: string): string {
  if (params.value?.filterParams.trim) {
    value = value.trim();
  }
  if (params.value?.filterParams.uppercase) {
    value = value.toUpperCase();
  }
  if (params.value?.filterParams.lowercase) {
    value = value.toLowerCase();
  }
  if (params.value?.filterParams.replace) {
    const { searchValue, replaceValue } = params.value.filterParams.replace;
    value = value.replaceAll(searchValue, replaceValue);
  }
  return value;
}

const params = shallowRef<
  IFloatingFilterParams & {
    filterParams: {
      uppercase?: boolean;
      lowercase?: boolean;
      trim?: boolean;
      replace: { searchValue: string | RegExp; replaceValue: string };
      mode?: 'equals' | 'contains';
    };
  }
>();

defineExpose({
  onParentModelChanged: updateLocalFilter,
});

onBeforeMount(() => {
  params.value = getCurrentInstance()?.proxy.$data.params;
  if (params.value === undefined) return;

  const filterModel = params.value.api.getColumnFilterModel<FilterModel>(params.value.column);
  updateLocalFilter(filterModel);
});

function updateLocalFilter(filterModel: FilterModel | null) {
  if (filterModel === null) {
    localFilterModel.value.filter = '';
  } else {
    localFilterModel.value = filterModel;
  }
}

watch(localFilterModel, applyFilter, { deep: true, immediate: true });

async function applyFilter() {
  if (params.value === undefined) return;
  localFilterModel.value.type = params.value?.filterParams.mode ?? 'equals';
  await params.value.api.setColumnFilterModel<FilterModel>(
    params.value.column,
    localFilterModel.value.filter === '' ? null : localFilterModel.value
  );
  params.value.api.onFilterChanged();
}
</script>

<template>
  <Wrapper :data-test-filter="params?.column.getColId()">
    <InputFormatted
      v-model="localFilterModel.filter"
      autocomplete="off"
      class="ag-input-field-input ag-text-field-input"
      :format-input="transform"
      :format-model="transform"
    />
  </Wrapper>
</template>
